import React, { useState } from "react";
import { useDropzone } from "react-dropzone";
import { useHistory } from "react-router";
import { useUnmount } from "react-use";
import { makeStyles } from "@mui/styles";
import {
  Box,
  Button,
  Grid,
  LinearProgress,
  MenuItem,
  Typography,
} from "@mui/material";
import { Form, Formik, FormikProps } from "formik";
import * as yup from "yup";

import FileIcon from "assets/images/icons/file.svg";
import UploadIcon from "assets/images/icons/upload.svg";
import FormikFormField from "components/shared/FormikFormField";
import { TARGET_UPLOAD } from "helpers/apis";
import useAxios from "helpers/axios";
import useApp from "hooks/useApp";
import { colors } from "theme";
import { IDiseaseType } from "types/diseaseType";
import { IForm, errors as CustomErrors } from "types/form";
import { IWorkflow } from "types/workflow";

interface IFileUploadForm extends IForm {
  workflowId: number | string;
  diseaseTypeId: number | string;
  fileUploaded: boolean;
}

const useStyles = makeStyles((theme) => ({
  fieldset: {
    border: "none",
    marginInlineStart: 0,
    marginInlineEnd: 0,
    paddingBlockStart: 0,
    paddingInlineStart: 0,
    paddingInlineEnd: 0,
    paddingBlockEnd: 0,
    minInlineSize: "inherit",
  },
  legend: {
    marginBottom: theme.spacing(2),
    paddingInlineStart: 0,
    paddingInlineEnd: 0,
  },
}));

const validationSchema = yup.object({
  workflowId: yup.number().required(CustomErrors.required).positive(),
  diseaseTypeId: yup.number().required(CustomErrors.required).positive(),
  fileUploaded: yup.bool().oneOf([true]).required(CustomErrors.required),
});

const UploadForm = ({
  diseaseTypes,
  workflows,
  formData: props,
  setDiseaseTypeId,
  setWorkflowId,
}: {
  formData: FormikProps<IFileUploadForm>;
  diseaseTypes: IDiseaseType[];
  workflows: IWorkflow[];
  setDiseaseTypeId: Function;
  setWorkflowId: Function;
}) => {
  const {
    dirty,
    isValid,
    setFieldError,
    setFieldValue,
    setFieldTouched,
    errors,
    touched,
    values,
  } = props;
  const history = useHistory();
  const [foundGenes, setFoundGenes] = useState<string[]>([]);
  const { fetchData, loading: isUploading } = useAxios({
    config: { url: TARGET_UPLOAD, method: "POST" },
    callOnInit: false,
  });
  const disableUpload = !values.diseaseTypeId || !values.workflowId;
  const { getRootProps, getInputProps } = useDropzone({
    accept: ".csv",
    multiple: false,
    disabled: disableUpload,
    noDrag: disableUpload,
    validator: (file: File) => {
      const formData = new FormData();
      formData.append("file", file);

      setFieldTouched("fileUploaded", true);
      fetchData(
        {
          data: formData,
        },
        ({ response }: { response: any }) => {
          if (
            Array.isArray(response.foundGenes) &&
            (response.foundGenes.length === 0 ||
              response.notFoundGenes?.length > 0)
          ) {
            setFieldError(
              "fileUploaded",
              CustomErrors.noFoundGenes(response.notFoundGenes)
            );
          } else {
            setFieldValue("fileUploaded", true);
            setFoundGenes(response.foundGenes);
          }
        },
        ({ error }: { error: string }) => {
          setFieldError("fileUploaded", error);
        }
      );

      return null;
    },
  });

  useUnmount(() => {
    setWorkflowId(values.workflowId);
    setDiseaseTypeId(values.diseaseTypeId);
  });

  const classes = useStyles();

  return (
    <Form>
      <Box
        component="fieldset"
        my={3}
        display="flex"
        flexDirection="column"
        alignItems="center"
        className={classes.fieldset}
      >
        <Typography
          variant="body1"
          component="legend"
          className={classes.legend}
        >
          Please select and upload to view matches
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormikFormField<IFileUploadForm>
              name="workflowId"
              label="Select Workflow"
              {...props}
              textFieldProps={{
                select: true,
                helperText: "",
                children: workflows.map((workflow: IWorkflow) => (
                  <MenuItem key={workflow.id} value={workflow.id}>
                    {workflow.name}
                  </MenuItem>
                )),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormikFormField
              name="diseaseTypeId"
              label="Select Disease Type"
              {...props}
              textFieldProps={{
                select: true,
                helperText: "",
                children: diseaseTypes.map((diseaseType: IDiseaseType) => (
                  <MenuItem key={diseaseType.id} value={diseaseType.id}>
                    {diseaseType.name}
                  </MenuItem>
                )),
              }}
            />
          </Grid>
        </Grid>

        <Box
          width={1}
          boxSizing="border-box"
          px={3}
          py={4}
          my={3}
          bgcolor={colors.grey[600]}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="227px"
          {...getRootProps({ className: "dropzone" })}
        >
          <input {...getInputProps()} />
          <img
            src={values.fileUploaded && !isUploading ? FileIcon : UploadIcon}
            alt={`${
              values.fileUploaded && !isUploading ? "File" : "Upload"
            } Icon`}
          />
          {isUploading && (
            <Box mt={2} width={1}>
              <LinearProgress />
            </Box>
          )}
          <Box my={2}>
            <Typography variant="body1" align="center">
              {isUploading
                ? "File is being validated."
                : errors.fileUploaded && touched.fileUploaded
                ? "File invalid. Try again."
                : values.fileUploaded
                ? "File uploaded successfully."
                : "Drag and drop files here."}
            </Typography>
          </Box>
          {!isUploading && (
            <>
              <Typography
                variant="body1"
                align="center"
                color={
                  errors.fileUploaded && touched.fileUploaded
                    ? "error"
                    : values.fileUploaded
                    ? "primary"
                    : "inherit"
                }
              >
                {errors.fileUploaded && touched.fileUploaded
                  ? errors.fileUploaded
                  : values.fileUploaded
                  ? "You can choose a new file."
                  : "File must be CSV file with HGNC symbol/fusion"}
              </Typography>
              <Box mt={2}>
                <Button
                  disabled={disableUpload}
                  variant="outlined"
                  color="primary"
                >
                  Browse
                </Button>
              </Box>
            </>
          )}
        </Box>

        <Button
          variant="contained"
          color="primary"
          size="large"
          disabled={!dirty || !isValid || foundGenes.length === 0}
          onClick={() => {
            history.push(
              `/evaluate-panel-options?workflow=${values.workflowId}&diseaseType=${values.diseaseTypeId}&foundGenes=${foundGenes}`
            );
          }}
        >
          View matches
        </Button>
      </Box>
    </Form>
  );
};

const FileUpload = ({
  workflowId,
  setWorkflowId,
  diseaseTypeId,
  setDiseaseTypeId,
}: {
  workflowId: number | string;
  diseaseTypeId: number | string;
  setWorkflowId: Function;
  setDiseaseTypeId: Function;
}) => {
  const { diseaseTypes, workflows } = useApp();
  const initialValues: IFileUploadForm = React.useMemo(
    () => ({
      workflowId: workflowId,
      diseaseTypeId: diseaseTypeId,
      fileUploaded: false,
    }),
    [workflowId, diseaseTypeId]
  );

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={() => {}}
      validationSchema={validationSchema}
    >
      {(formData: FormikProps<IFileUploadForm>) => (
        <UploadForm
          formData={formData}
          diseaseTypes={diseaseTypes}
          workflows={workflows}
          setWorkflowId={setWorkflowId}
          setDiseaseTypeId={setDiseaseTypeId}
        />
      )}
    </Formik>
  );
};

export default React.memo(FileUpload);
